
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, Job } from '../../../lib/supabase';

interface JobPreviewProps {
  job: Job;
}

export default function JobPreview({ job }: JobPreviewProps) {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    coverLetter: '',
  });
  const [resumeFile, setResumeFile] = useState<File | null>(null);

  // Parse requirements and benefits from TEXT to array
  const requirementsList = job.requirements ? job.requirements.split('\n').filter(r => r.trim()) : [];
  const benefitsList = job.benefits ? job.benefits.split('\n').filter(b => b.trim()) : [];

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        setFormData((prev) => ({
          ...prev,
          email: session.user.email || '',
        }));
      }
    });
  }, []);

  const triggerConfetti = () => {
    // Create confetti effect using canvas
    const canvas = document.createElement('canvas');
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '9999';
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: any[] = [];
    const colors = ['#6366f1', '#8b5cf6', '#F59E0B', '#EF4444', '#10B981'];

    for (let i = 0; i < 100; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height - canvas.height,
        r: Math.random() * 6 + 2,
        d: Math.random() * 100,
        color: colors[Math.floor(Math.random() * colors.length)],
        tilt: Math.random() * 10 - 10,
        tiltAngleIncremental: Math.random() * 0.07 + 0.05,
        tiltAngle: 0,
      });
    }

    let animationFrame: number;
    const draw = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p, i) => {
        ctx.beginPath();
        ctx.lineWidth = p.r / 2;
        ctx.strokeStyle = p.color;
        ctx.moveTo(p.x + p.tilt + p.r, p.y);
        ctx.lineTo(p.x + p.tilt, p.y + p.tilt + p.r);
        ctx.stroke();

        p.tiltAngle += p.tiltAngleIncremental;
        p.y += (Math.cos(p.d) + 3 + p.r / 2) / 2;
        p.tilt = Math.sin(p.tiltAngle - i / 3) * 15;

        if (p.y > canvas.height) {
          particles.splice(i, 1);
        }
      });

      if (particles.length > 0) {
        animationFrame = requestAnimationFrame(draw);
      } else {
        document.body.removeChild(canvas);
      }
    };

    draw();
  };

  const handleApply = async () => {
    if (!user) {
      alert('Please sign in to apply for jobs');
      return;
    }

    setShowApplicationForm(true);
  };

  const handleSubmitApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    setApplying(true);

    try {
      let resumeUrl = '';

      // Upload resume to Supabase Storage with path: public/{job_id}/{user_id}_{filename}
      if (resumeFile) {
        const fileExt = resumeFile.name.split('.').pop();
        const fileName = `public/${job.id}/${user.id}_${Date.now()}.${fileExt}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('resumes')
          .upload(fileName, resumeFile);

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: urlData } = supabase.storage
          .from('resumes')
          .getPublicUrl(fileName);

        resumeUrl = urlData.publicUrl;
      }

      // Submit application
      const { error: applicationError } = await supabase
        .from('applications')
        .insert([
          {
            job_id: job.id,
            user_id: user.id,
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            cover_letter: formData.coverLetter,
            resume_url: resumeUrl,
          },
        ]);

      if (applicationError) throw applicationError;

      setApplicationSuccess(true);
      triggerConfetti();

      // Show success toast
      const toast = document.createElement('div');
      toast.className = 'fixed top-24 right-4 bg-[#6366f1] text-white px-6 py-4 rounded shadow-2xl z-50 animate-slide-in-right';
      toast.innerHTML = `
        <div class="flex items-center space-x-3">
          <i class="ri-check-line text-2xl"></i>
          <div>
            <div class="font-semibold">Application Sent!</div>
            <div class="text-sm opacity-90">Your application has been sent to ${job.company}</div>
          </div>
        </div>
      `;
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 4000);

      setTimeout(() => {
        setShowApplicationForm(false);
        setApplicationSuccess(false);
        setFormData({
          firstName: '',
          lastName: '',
          email: user.email || '',
          phone: '',
          coverLetter: '',
        });
        setResumeFile(null);
      }, 2000);
    } catch (error: any) {
      console.error('Application error:', error);
      alert(error.message || 'Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
      <div className="bg-[#1c1c1c] border border-[#333333] rounded overflow-hidden max-h-[calc(100vh-160px)] overflow-y-auto custom-scrollbar">
        <div className="p-8">
          {/* Job Header */}
          <div className="mb-6">
            <h2 className="text-3xl font-semibold text-white mb-3">{job.title}</h2>
            <div className="flex flex-wrap items-center gap-4 text-[#a0a0a0] mb-4">
              <span className="flex items-center font-medium text-white">
                <i className="ri-building-line mr-2"></i>
                {job.company}
              </span>
              <span className="flex items-center">
                <i className="ri-map-pin-line mr-2"></i>
                {job.location}
              </span>
              <div className="flex items-center">
                <i className="ri-star-fill text-yellow-500 mr-1"></i>
                <span>4.{Math.floor(Math.random() * 5 + 3)}</span>
              </div>
            </div>

            {/* Key Info Badges */}
            <div className="flex flex-wrap gap-3">
              <span className="px-4 py-2 bg-[#2d2d2d] text-white rounded border border-[#333333]">
                <i className="ri-briefcase-line mr-2"></i>
                {job.job_type}
              </span>
              <span className="px-4 py-2 bg-[#2d2d2d] text-white rounded border border-[#333333]">
                <i className="ri-time-line mr-2"></i>
                {job.experience_level}
              </span>
              <span className="px-4 py-2 bg-green-500/10 text-green-400 font-semibold rounded border border-green-500/30">
                <i className="ri-money-dollar-circle-line mr-2"></i>
                {job.salary_range}
              </span>
            </div>
          </div>

          {/* Apply Button */}
          <div className="mb-8">
            <button
              onClick={handleApply}
              className="w-full py-3 bg-[#6366f1] text-white font-semibold rounded hover:bg-[#5558e3] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-send-plane-fill mr-2"></i>
              Apply Now
            </button>
          </div>

          {/* Tags */}
          {job.tags && job.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8 pb-8 border-b border-[#333333]">
              {job.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-[#6366f1]/10 border border-[#6366f1]/30 text-[#6366f1] text-sm rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Description */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-white mb-4">Job Description</h3>
            <p className="text-[#a0a0a0] leading-relaxed">{job.description}</p>
          </div>

          {/* Requirements */}
          {requirementsList.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-white mb-4">Requirements</h3>
              <ul className="space-y-3">
                {requirementsList.map((req, index) => (
                  <li key={index} className="flex items-start text-[#a0a0a0]">
                    <i className="ri-checkbox-circle-fill text-[#6366f1] mr-3 mt-1 flex-shrink-0"></i>
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Benefits */}
          {benefitsList.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-white mb-4">Benefits</h3>
              <ul className="space-y-3">
                {benefitsList.map((benefit, index) => (
                  <li key={index} className="flex items-start text-[#a0a0a0]">
                    <i className="ri-star-fill text-green-500 mr-3 mt-1 flex-shrink-0"></i>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Company Info */}
          <div className="bg-[#2d2d2d] rounded p-6 border border-[#333333]">
            <h3 className="text-lg font-semibold text-white mb-3">About {job.company}</h3>
            <p className="text-[#a0a0a0] leading-relaxed mb-4">
              Join our innovative team and work on cutting-edge projects that make a real impact. We value creativity, collaboration, and continuous learning.
            </p>
            <button
              onClick={() => navigate(`/jobs/${job.id}`)}
              className="text-[#6366f1] hover:underline font-medium cursor-pointer"
            >
              View full job details
              <i className="ri-arrow-right-line ml-1"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Application Form Modal */}
      {showApplicationForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative w-full max-w-2xl bg-[#1c1c1c] rounded shadow-2xl max-h-[90vh] overflow-y-auto custom-scrollbar border border-[#333333]">
            <div className="sticky top-0 bg-[#1c1c1c] border-b border-[#333333] px-8 py-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-white">Apply for {job.title}</h2>
                <p className="text-[#a0a0a0]">at {job.company}</p>
              </div>
              <button
                onClick={() => setShowApplicationForm(false)}
                className="text-[#a0a0a0] hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-8">
              {applicationSuccess ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-[#6366f1] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-4xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-2">Application Submitted!</h3>
                  <p className="text-[#a0a0a0]">We'll be in touch soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmitApplication} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={(e) =>
                          setFormData({ ...formData, firstName: e.target.value })
                        }
                        className="w-full px-4 py-3 bg-[#2d2d2d] border border-[#333333] rounded text-white placeholder-[#a0a0a0] focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={(e) =>
                          setFormData({ ...formData, lastName: e.target.value })
                        }
                        className="w-full px-4 py-3 bg-[#2d2d2d] border border-[#333333] rounded text-white placeholder-[#a0a0a0] focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className="w-full px-4 py-3 bg-[#2d2d2d] border border-[#333333] rounded text-white placeholder-[#a0a0a0] focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      className="w-full px-4 py-3 bg-[#2d2d2d] border border-[#333333] rounded text-white placeholder-[#a0a0a0] focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Upload Resume * (PDF, DOC, DOCX)
                    </label>
                    <input
                      type="file"
                      required
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
                      className="w-full px-4 py-3 bg-[#2d2d2d] border border-[#333333] rounded text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-[#6366f1] file:text-white file:cursor-pointer cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Cover Letter
                    </label>
                    <textarea
                      value={formData.coverLetter}
                      onChange={(e) =>
                        setFormData({ ...formData, coverLetter: e.target.value })
                      }
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-[#2d2d2d] border border-[#333333] rounded text-white placeholder-[#a0a0a0] focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1] resize-none"
                      placeholder="Tell us why you're a great fit..."
                    />
                    <p className="text-xs text-[#a0a0a0] mt-1">
                      {formData.coverLetter.length}/500 characters
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={applying}
                    className="w-full py-3 bg-[#6366f1] text-white font-semibold rounded hover:bg-[#5558e3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {applying ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Submitting...
                      </span>
                    ) : (
                      'Submit Application'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
